import { useState } from 'react';

import { formatNumber } from '@shared/format';
import { cn } from '@shared/ui/cn';
import { DTable, DTableBody, DTableHead, DTableRowHeader, DTableScroll } from '@shared/ui/dtable';
import { CheckIcon, PlusIcon } from '@shared/ui/icons';
import { StateBadge } from '@shared/ui/StateBadge';

import {
  formatDuration,
  formatFileSize,
  resolveAssetReadiness,
  resolveEncodeOutcome,
  resolveRenditionState,
} from '../_shared/constants';
import { toAcceptAttribute } from '../_shared/queries';
import type { MediaAsset, UploadPolicy } from '../_shared/types';
import { ArtworkControl } from './ContentAssets.ArtworkControl';
import { copy } from './copy';
import { useRetryEncodeJob } from './mutations';
import { useDocuments, useEncodeJobs, useRenditions } from './queries';

/**
 * One asset — its head, its ladder, and the tracks that translate it.
 *
 * **Renditions are four rungs, three columns, and no delivery column.**
 * `DELIVERY_MODES` is `stream` alone, so a column repeating one value on every
 * row separates no row from any other and a select offering one option asks a
 * question with no second answer. The fact is stated once at the foot of the
 * page instead.
 *
 * **A subtitle lives inside the asset it translates**, not in a section of its
 * own: a document is a child of what it belongs to, so it consumes no asset slot
 * — which is exactly what lets a film bounded at one asset carry tracks at all —
 * and the destination needs no picker, because the operator opened this asset's
 * block.
 *
 * **The bin is the last control and the quietest.** A permanently red button in
 * every row of a nine-row list turns the page into a continuous warning, and a
 * warning that is always on is not read.
 */
export function AssetRow({
  asset,
  uploadPolicy,
  ordinal,
  canReorder,
  freeControl,
  onEdit,
  onRemove,
  onMove,
  onToggleFree,
  onAddDocument,
  onRemoveDocument,
  busyDocument,
}: {
  asset: MediaAsset;
  uploadPolicy: UploadPolicy | undefined;
  /** `null` on the trailer and on a film's single asset — an ordinal over one is not a fact. */
  ordinal: number | null;
  canReorder: boolean;
  /** The trailer is free **by kind**, so it renders a badge and no chip. */
  freeControl: 'chip' | 'always-free' | 'none';
  onEdit: () => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
  onToggleFree: () => void;
  onAddDocument: (file: File) => void;
  onRemoveDocument: (documentId: string, label: string) => void;
  busyDocument: boolean;
}) {
  const [renditionsOpen, setRenditionsOpen] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState(false);

  // Both blocks are folded and both fetch on opening: nine assets × four rungs
  // is thirty-six rows that would bury the list, and fetching them on arrival
  // would spend the requests on rows nobody asked to see.
  const renditions = useRenditions(asset.id, renditionsOpen);
  // Read beside the ladder rather than behind a second disclosure: the ladder
  // is exactly where a failed job is invisible, so the answer has to arrive
  // with the rows that hide it.
  const encodeJobs = useEncodeJobs(asset.id, renditionsOpen);
  const documents = useDocuments('MEDIA_ASSET', asset.id, documentsOpen);

  // The newest attempt is the one that describes the ladder on screen; earlier
  // attempts are history the operator cannot act on.
  const encodeJob = (encodeJobs.data ?? [])[0];
  const retryEncode = useRetryEncodeJob(asset.id);

  // Seconds on the record, milliseconds nowhere on this row: `content` stores
  // `duration_seconds` and that is the only figure either service carries for an
  // asset. There is no size and no container beside it — no column holds either,
  // so the line said nothing on every row rather than sometimes.
  const duration = formatDuration(asset.duration_seconds === null ? null : asset.duration_seconds * 1000);

  // **The one fact this row withheld.** The encode job's state was drawn inside
  // the folded «النسخ» disclosure and fetched only on opening it, so a closed
  // row — the state every row is in on arrival — said nothing about a file that
  // was still transcoding or whose upload never finished. Both states ride on
  // the asset payload the page already holds, and the head is where the reader
  // is looking when they wonder why publication was refused.
  const readiness = resolveAssetReadiness(asset.storage_state, asset.encode_state);

  return (
    <li className="grid gap-3 rounded-md border border-border bg-surface p-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {ordinal === null ? null : (
          <span className="flex items-center gap-1">
            {/*
              **Arabic-Indic, like every other figure a person reads here.** The
              ordinal went to the page as a raw JavaScript number, so the season
              count above it read «٣ مواسم» while the episodes under it counted
              «1، 2، 3» — two digit systems in one panel, on a surface the
              product declares Arabic-only.
            */}
            <b className="text-sm [font-variant-numeric:tabular-nums]">
              <bdi>{formatNumber(ordinal)}</bdi>
            </b>
            {canReorder ? (
              <span className="flex">
                <IconButton label={copy.asset.moveUp(asset.title)} onPress={() => onMove('up')}>
                  <ArrowUp />
                </IconButton>
                <IconButton label={copy.asset.moveDown(asset.title)} onPress={() => onMove('down')}>
                  <ArrowDown />
                </IconButton>
              </span>
            ) : null}
          </span>
        )}

        <div className="min-w-0 grow">
          <p className="mb-0 max-w-none truncate text-sm text-ink">{asset.title}</p>
          <p className="mb-0 max-w-none text-xs text-ink-muted">
            {duration ? <bdi className="mono">{duration}</bdi> : null}
          </p>
        </div>

        {readiness ? <StateBadge kind={readiness.kind}>{readiness.label}</StateBadge> : null}

        {freeControl === 'always-free' ? (
          <StateBadge kind="success">{copy.trailer.alwaysFree}</StateBadge>
        ) : null}

        {/*
          **A toggle that looks like one.** The chip carried the same six words
          in both states and differed only by `aria-pressed` — correct for a
          screen reader, invisible to an eye, so two files reading «مجّانيّة بلا
          اشتراك» side by side were one marked and one not. The accessible name
          stays put, because a toggle button that renames itself tells the
          reader who just pressed it that a different control is now under their
          finger; what changes is the mark beside the name, and a resting state
          that reads as an offer rather than as a fact.
        */}
        {freeControl === 'chip' ? (
          <button
            type="button"
            aria-pressed={asset.is_free_preview}
            onClick={onToggleFree}
            className={cn(
              'hit-target flex items-center gap-1 rounded-pill border px-3 text-xs',
              asset.is_free_preview
                ? 'border-accent bg-accent-soft font-semibold text-accent-text'
                : 'border-dashed border-border bg-surface-raised text-ink-muted hover:bg-hover-bg',
            )}
          >
            {asset.is_free_preview ? (
              <CheckIcon className="size-[14px] flex-none" aria-hidden="true" />
            ) : (
              <PlusIcon className="size-[14px] flex-none" aria-hidden="true" />
            )}
            <span>{copy.asset.free}</span>
          </button>
        ) : null}

        <button
          type="button"
          onClick={onEdit}
          className="hit-target rounded-sm px-3 text-xs font-semibold text-ink hover:bg-hover-bg"
        >
          {copy.asset.edit}
        </button>

        {/* Quiet in rest, loud in confirmation, visible in refusal. */}
        <IconButton label={copy.asset.remove(asset.title)} danger onPress={onRemove}>
          <Bin />
        </IconButton>
      </div>

      <details onToggle={(event) => setRenditionsOpen(event.currentTarget.open)}>
        <summary className="cursor-pointer text-xs text-ink-muted">{copy.asset.renditions}</summary>

        <div className="mt-2">
          <DTableScroll>
            <DTable caption={copy.asset.renditionsCaption(asset.title)} minInlineSize="18rem">
              <DTableHead>
                <tr>
                  <th scope="col">{copy.asset.renditionColumns.label}</th>
                  <th scope="col">{copy.asset.renditionColumns.size}</th>
                  <th scope="col">{copy.asset.renditionColumns.state}</th>
                </tr>
              </DTableHead>
              <DTableBody>
                {(renditions.data ?? []).map((rung) => {
                  const state = resolveRenditionState(rung.state);
                  const rungSize = formatFileSize(rung.size_bytes);

                  return (
                    <tr key={rung.id}>
                      <DTableRowHeader>
                        <bdi className="mono">{rung.label}</bdi>
                      </DTableRowHeader>
                      {/* A size is not known for a file that does not exist yet. */}
                      <td className="whitespace-nowrap">
                        {rungSize ? <bdi>{rungSize}</bdi> : <span className="text-ink-muted">—</span>}
                      </td>
                      <td>
                        <StateBadge kind={state.kind}>{state.label}</StateBadge>
                      </td>
                    </tr>
                  );
                })}
              </DTableBody>
            </DTable>
          </DTableScroll>

          {encodeJob ? (
            <section aria-label={copy.asset.encode.heading} className="mt-4 grid gap-2">
              <span className="flex flex-wrap items-center gap-2 text-xs font-semibold text-ink">
                {copy.asset.encode.heading}
                <StateBadge kind={resolveEncodeOutcome(encodeJob.state).kind}>
                  {resolveEncodeOutcome(encodeJob.state).label}
                </StateBadge>
              </span>

              <DTableScroll>
                <DTable caption={copy.asset.encode.caption(asset.title)} minInlineSize="16rem">
                  <DTableHead>
                    <tr>
                      <th scope="col">{copy.asset.encode.columns.rung}</th>
                      <th scope="col">{copy.asset.encode.columns.outcome}</th>
                    </tr>
                  </DTableHead>
                  <DTableBody>
                    {encodeJob.requested_ladder.map(([rung, outcome]) => {
                      const resolved = resolveEncodeOutcome(outcome);

                      return (
                        <tr key={rung}>
                          <DTableRowHeader>
                            <bdi className="mono">{rung}</bdi>
                          </DTableRowHeader>
                          <td>
                            <StateBadge kind={resolved.kind}>{resolved.label}</StateBadge>
                          </td>
                        </tr>
                      );
                    })}
                  </DTableBody>
                </DTable>
              </DTableScroll>

              <p className="mb-0 max-w-none text-xs text-ink-muted">
                {copy.asset.encode.attempts(formatNumber(encodeJob.attempt_count))}
              </p>

              {encodeJob.state === 'FAILED' ? (
                <>
                  {/*
                    The reason, whatever there is of it. The service records
                    `errorDetail: null` on most failures, so the code is often
                    the whole answer — and saying that is still an answer, where
                    four `SKIPPED` rungs and no badge were not.
                  */}
                  <p className="mb-0 max-w-none text-xs text-ink-muted">
                    {encodeJob.error_code
                      ? copy.asset.encode.reason(encodeJob.error_code)
                      : copy.asset.encode.noReason}
                  </p>
                  {encodeJob.error_detail ? (
                    <p className="mb-0 max-w-none text-xs text-ink-muted">{encodeJob.error_detail}</p>
                  ) : null}

                  <button
                    type="button"
                    disabled={retryEncode.isPending}
                    onClick={() => retryEncode.mutate(encodeJob.id)}
                    className="hit-target w-fit rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink hover:bg-surface-overlay disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {retryEncode.isPending ? copy.asset.encode.retrying : copy.asset.encode.retry}
                  </button>
                </>
              ) : null}

              {/*
                Outside the failure branch, deliberately. Accepting the retry
                moves the job out of `FAILED`, which unmounts the block that
                held the button — so an operator pressed a control, watched it
                disappear, and was told nothing at all. The acknowledgement is
                about the request, not about the state it was sent from.
              */}
              {retryEncode.isSuccess ? (
                <p role="status" className="mb-0 max-w-none text-xs text-ink-muted">
                  {copy.asset.encode.retried}
                </p>
              ) : null}
            </section>
          ) : null}
        </div>
      </details>

      {/*
        The still, between the ladder and the tracks — the row's own image
        belongs with the row's own facts, and it is folded like everything else
        here so nine episodes do not open nine image reads on arrival.
      */}
      <ArtworkControl
        subjectType="MEDIA_ASSET"
        subjectId={asset.id}
        subjectName={asset.title}
        uploadPolicy={uploadPolicy}
      />

      <details onToggle={(event) => setDocumentsOpen(event.currentTarget.open)}>
        <summary className="cursor-pointer text-xs text-ink-muted">{copy.asset.documents}</summary>

        <div className="mt-2 grid gap-2">
          {(documents.data ?? []).length === 0 ? (
            <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.asset.documentsEmpty}</p>
          ) : (
            <ul className="grid list-none gap-2 p-0">
              {(documents.data ?? []).map((document) => (
                <li
                  key={document.id}
                  className="flex flex-wrap items-center gap-3 rounded-sm border border-border bg-surface-raised p-2"
                >
                  <span className="grow text-sm text-ink">{document.label}</span>
                  <span className="text-xs text-ink-muted">
                    {document.format ? <bdi className="mono">{document.format}</bdi> : null}
                    {formatFileSize(document.size_bytes) ? (
                      <>
                        {' '}
                        · <bdi>{formatFileSize(document.size_bytes)}</bdi>
                      </>
                    ) : null}
                  </span>
                  <IconButton
                    label={copy.asset.documentRemove(document.label)}
                    danger
                    onPress={() => onRemoveDocument(document.id, document.label)}
                  >
                    <Bin />
                  </IconButton>
                </li>
              ))}
            </ul>
          )}

          <label className="hit-target inline-flex w-fit cursor-pointer items-center rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink hover:bg-surface-overlay">
            {busyDocument ? copy.asset.uploading : copy.asset.documentAdd}
            <input
              type="file"
              className="sr-only"
              accept={toAcceptAttribute(uploadPolicy?.accepted_document_formats)}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onAddDocument(file);
                event.target.value = '';
              }}
            />
          </label>

          {/* Attaching is not synchronising, and the hint is where it is said. */}
          <p className="mb-0 max-w-measure text-xs text-ink-muted">
            {copy.asset.documentHint(
              asset.title,
              (uploadPolicy?.accepted_document_formats ?? []).join(' · '),
            )}
          </p>
        </div>
      </details>
    </li>
  );
}

function IconButton({
  label,
  danger = false,
  onPress,
  children,
}: {
  label: string;
  danger?: boolean;
  onPress: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onPress}
      className={cn(
        'hit-target flex items-center justify-center rounded-sm text-ink-muted',
        danger ? 'hover:bg-danger-soft hover:text-danger' : 'hover:bg-hover-bg hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function Glyph({ children }: { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.125em"
      height="1.125em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

function ArrowUp() {
  return (
    <Glyph>
      <path d="M12 19V5M6 11l6-6 6 6" />
    </Glyph>
  );
}

function ArrowDown() {
  return (
    <Glyph>
      <path d="M12 5v14M6 13l6 6 6-6" />
    </Glyph>
  );
}

function Bin() {
  return (
    <Glyph>
      <path d="M4 7h16" />
      <path d="M9.5 7V5.5A1.5 1.5 0 0111 4h2a1.5 1.5 0 011.5 1.5V7" />
      <path d="M6.5 7l.8 12a1.5 1.5 0 001.5 1.4h6.4a1.5 1.5 0 001.5-1.4l.8-12" />
    </Glyph>
  );
}
